import { useState, useEffect, useCallback } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { FileCheck, Clock, History, Plus, RefreshCw, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
    draft:      "bg-zinc-100 text-zinc-700 border-zinc-200",
    sent:       "bg-blue-50 text-blue-700 border-blue-200",
    signed:     "bg-emerald-50 text-emerald-700 border-emerald-200",
    expired:    "bg-amber-50 text-amber-700 border-amber-200",
    terminated: "bg-red-50 text-red-700 border-red-200",
};

const STATUS_LABELS = {
    draft: "Draft", sent: "Sent", signed: "Signed",
    expired: "Expired", terminated: "Terminated",
};

const MANDATE_LABELS = {
    sell_side: "Sell-side", buy_side: "Buy-side",
    advisory: "Advisory", other: "Other",
};

function fmtDate(iso) {
    if (!iso) return null;
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function StatusBadge({ status }) {
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${STATUS_STYLES[status] || STATUS_STYLES.draft}`}>
            {status === "signed" && <FileCheck className="w-3 h-3" />}
            {(status === "expired" || status === "terminated") && <Clock className="w-3 h-3" />}
            {STATUS_LABELS[status] || status}
        </span>
    );
}

function NewLetterForm({ dealId, onCreated, onClose, priorLetter }) {
    const [form, setForm] = useState({
        mandate_type: priorLetter?.mandate_type || "sell_side",
        scope_description: priorLetter?.scope_description || "",
        termination_notice_days: "",
        status: "draft",
        signatory_name: "",
        signatory_role: "",
        expiry_date: "",
        signed_date: "",
    });
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                ...form,
                termination_notice_days: form.termination_notice_days ? parseInt(form.termination_notice_days) : null,
                expiry_date: form.expiry_date || null,
                signed_date: form.signed_date || null,
                signatory_name: form.signatory_name || null,
                signatory_role: form.signatory_role || null,
                scope_description: form.scope_description || null,
            };
            const created = await api.post(`/deals/${dealId}/engagement-letters`, payload);
            toast.success("Engagement letter created");
            onCreated(created.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to create letter");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide">Mandate type</Label>
                    <Select value={form.mandate_type} onValueChange={v => setForm(f => ({ ...f, mandate_type: v }))}>
                        <SelectTrigger data-testid="el-mandate-type" className="h-8 text-sm">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="sell_side">Sell-side</SelectItem>
                            <SelectItem value="buy_side">Buy-side</SelectItem>
                            <SelectItem value="advisory">Advisory</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide">Status</Label>
                    <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                        <SelectTrigger data-testid="el-status" className="h-8 text-sm">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="sent">Sent</SelectItem>
                            <SelectItem value="signed">Signed</SelectItem>
                            <SelectItem value="expired">Expired</SelectItem>
                            <SelectItem value="terminated">Terminated</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground uppercase tracking-wide">Scope of engagement</Label>
                <Textarea
                    data-testid="el-scope"
                    value={form.scope_description}
                    onChange={e => setForm(f => ({ ...f, scope_description: e.target.value }))}
                    placeholder="Describe what the firm is engaged to do…"
                    className="text-sm min-h-[72px] resize-none"
                />
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide">Expiry date</Label>
                    <Input data-testid="el-expiry-date" type="date" className="h-8 text-sm"
                        value={form.expiry_date}
                        onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide">Notice period (days)</Label>
                    <Input data-testid="el-notice-days" type="number" min="0" className="h-8 text-sm"
                        value={form.termination_notice_days}
                        onChange={e => setForm(f => ({ ...f, termination_notice_days: e.target.value }))}
                        placeholder="e.g. 30" />
                </div>
            </div>

            {form.status === "signed" && (
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground uppercase tracking-wide">Signed date</Label>
                        <Input data-testid="el-signed-date" type="date" className="h-8 text-sm"
                            value={form.signed_date}
                            onChange={e => setForm(f => ({ ...f, signed_date: e.target.value }))} />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground uppercase tracking-wide">Signatory name</Label>
                        <Input data-testid="el-signatory-name" className="h-8 text-sm"
                            value={form.signatory_name}
                            onChange={e => setForm(f => ({ ...f, signatory_name: e.target.value }))}
                            placeholder="e.g. Jane Smith" />
                    </div>
                </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" size="sm" onClick={onClose} className="text-xs">Cancel</Button>
                <Button data-testid="el-save-btn" size="sm" onClick={handleSave} disabled={saving} className="text-xs bg-gold/90 hover:bg-gold text-black">
                    {saving ? "Saving…" : "Create letter"}
                </Button>
            </div>
        </div>
    );
}

function HistoryModal({ dealId, onClose, onRefresh }) {
    const [letters, setLetters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [patching, setPatching] = useState(null);

    useEffect(() => {
        api.get(`/deals/${dealId}/engagement-letters`)
            .then(r => setLetters(r.data))
            .catch(() => toast.error("Failed to load history"))
            .finally(() => setLoading(false));
    }, [dealId]);

    const patchStatus = async (id, status) => {
        setPatching(id);
        try {
            await api.patch(`/engagement-letters/${id}`, { status });
            const updated = await api.get(`/deals/${dealId}/engagement-letters`);
            setLetters(updated.data);
            onRefresh();
            toast.success("Status updated");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Update failed");
        } finally {
            setPatching(null);
        }
    };

    return (
        <div className="space-y-3">
            {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
            {!loading && letters.length === 0 && (
                <p className="text-sm text-muted-foreground">No engagement letters yet.</p>
            )}
            {letters.map((el, idx) => (
                <div key={el.id} data-testid={`el-history-item-${idx}`}
                    className={`rounded-lg border p-3 space-y-2 ${el.is_current ? "border-gold/40 bg-gold/5" : "border-border bg-muted/20 opacity-75"}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2 flex-wrap">
                            <StatusBadge status={el.status} />
                            {!el.is_current && (
                                <span className="text-[10px] text-muted-foreground italic">superseded</span>
                            )}
                            {el.is_current && (
                                <span className="text-[10px] text-gold font-medium">current</span>
                            )}
                        </div>
                        <span className="text-[10px] text-muted-foreground">{fmtDate(el.created_at)}</span>
                    </div>

                    <div className="text-xs text-muted-foreground grid grid-cols-2 gap-x-4 gap-y-0.5">
                        <span>Type: <span className="text-foreground">{MANDATE_LABELS[el.mandate_type]}</span></span>
                        {el.signed_date && <span>Signed: <span className="text-foreground">{fmtDate(el.signed_date)}</span></span>}
                        {el.expiry_date && <span>Expires: <span className="text-foreground">{fmtDate(el.expiry_date)}</span></span>}
                        {el.termination_notice_days && <span>Notice: <span className="text-foreground">{el.termination_notice_days}d</span></span>}
                        {el.signatory_name && <span>Signatory: <span className="text-foreground">{el.signatory_name}</span></span>}
                        {el.supersedes_id && <span className="col-span-2 text-[10px]">Supersedes: {el.supersedes_id.slice(0, 8)}…</span>}
                    </div>

                    {el.scope_description && (
                        <p className="text-xs text-muted-foreground italic border-t border-border/40 pt-2">{el.scope_description}</p>
                    )}

                    {el.is_current && el.status !== "signed" && (
                        <div className="flex gap-1.5 pt-1">
                            <Button size="sm" variant="outline" className="h-6 text-[10px] px-2"
                                disabled={patching === el.id}
                                data-testid={`el-mark-signed-${el.id}`}
                                onClick={() => patchStatus(el.id, "signed")}>
                                Mark signed
                            </Button>
                        </div>
                    )}
                    {el.is_current && el.status === "signed" && (
                        <div className="flex gap-1.5 pt-1">
                            <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 text-amber-700 border-amber-200"
                                disabled={patching === el.id}
                                data-testid={`el-mark-expired-${el.id}`}
                                onClick={() => patchStatus(el.id, "expired")}>
                                Mark expired
                            </Button>
                            <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 text-red-700 border-red-200"
                                disabled={patching === el.id}
                                data-testid={`el-mark-terminated-${el.id}`}
                                onClick={() => patchStatus(el.id, "terminated")}>
                                Terminate
                            </Button>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

export default function EngagementLetterPanel({ deal, onDealUpdate }) {
    const { user } = useAuth();
    const [letters, setLetters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNew, setShowNew] = useState(false);
    const [showHistory, setShowHistory] = useState(false);

    const canEdit = user?.role === "admin" || user?.role === "broker";

    const loadLetters = useCallback(() => {
        if (!deal?.id) return;
        setLoading(true);
        api.get(`/deals/${deal.id}/engagement-letters`)
            .then(r => setLetters(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [deal?.id]);

    useEffect(() => { loadLetters(); }, [loadLetters]);

    const current = letters.find(l => l.is_current);
    const canRenew = current && (current.status === "signed" || current.status === "expired");

    const handleRenew = async () => {
        try {
            await api.post(`/engagement-letters/${current.id}/renew`);
            toast.success("Renewal draft created");
            loadLetters();
            onDealUpdate?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to create renewal");
        }
    };

    const handleCreated = () => {
        setShowNew(false);
        loadLetters();
        onDealUpdate?.();
    };

    return (
        <div data-testid="engagement-letter-panel" className="flex flex-col gap-2">
            <div className="flex items-center gap-2 flex-wrap">
                {loading ? (
                    <span className="text-xs text-muted-foreground">Loading…</span>
                ) : current ? (
                    <>
                        <StatusBadge status={current.status} />
                        {current.expiry_date && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Clock className="w-3 h-3" />Expires {fmtDate(current.expiry_date)}
                            </span>
                        )}
                    </>
                ) : (
                    <span className="text-xs text-muted-foreground italic">No engagement letter</span>
                )}
            </div>

            <div className="flex items-center gap-1.5 flex-wrap">
                {letters.length > 0 && (
                    <button
                        type="button"
                        data-testid="el-view-history-btn"
                        onClick={() => setShowHistory(true)}
                        className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors">
                        <History className="w-3 h-3" />
                        History ({letters.length})
                        <ChevronRight className="w-3 h-3" />
                    </button>
                )}
                {canEdit && !current && (
                    <Button size="sm" variant="outline" className="h-6 text-[10px] px-2"
                        data-testid="el-new-btn"
                        onClick={() => setShowNew(true)}>
                        <Plus className="w-3 h-3 mr-1" />New engagement letter
                    </Button>
                )}
                {canEdit && canRenew && (
                    <Button size="sm" variant="outline" className="h-6 text-[10px] px-2"
                        data-testid="el-renew-btn"
                        onClick={handleRenew}>
                        <RefreshCw className="w-3 h-3 mr-1" />Renew
                    </Button>
                )}
                {canEdit && current && !canRenew && (
                    <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2 text-muted-foreground"
                        data-testid="el-new-btn"
                        onClick={() => setShowNew(true)}>
                        <Plus className="w-3 h-3 mr-1" />New letter
                    </Button>
                )}
            </div>

            {/* New letter modal */}
            <Dialog open={showNew} onOpenChange={setShowNew}>
                <DialogContent className="max-w-lg" data-testid="el-new-modal">
                    <DialogHeader>
                        <DialogTitle className="text-sm font-semibold">New engagement letter</DialogTitle>
                    </DialogHeader>
                    <NewLetterForm
                        dealId={deal.id}
                        onCreated={handleCreated}
                        onClose={() => setShowNew(false)}
                        priorLetter={null}
                    />
                </DialogContent>
            </Dialog>

            {/* History modal */}
            <Dialog open={showHistory} onOpenChange={setShowHistory}>
                <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="el-history-modal">
                    <DialogHeader>
                        <DialogTitle className="text-sm font-semibold">Engagement letter history</DialogTitle>
                    </DialogHeader>
                    <HistoryModal
                        dealId={deal.id}
                        onClose={() => setShowHistory(false)}
                        onRefresh={() => { loadLetters(); onDealUpdate?.(); }}
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
}
